import { z } from 'zod';

/**
 * Every setting a TR-069 router reports, read off its own parameter tree
 * (operator, 2026-10-05: "make the config tab of onu details page dynamic,
 * show us and allow us to configure whatever the onu allows").
 *
 * The named Configs panels (WAN, LAN, WiFi …) carry platform logic and stay;
 * this is the rest of the router, in the router's own shape. Nothing here is
 * a per-model list: a leaf is shown because the CPE reports it, and offered
 * for editing because the CPE marks it writable — and the platform has no
 * reason to withhold it (`guard`).
 */

/** Why the platform will not write a leaf from this editor, and where to change it instead. */
export const deviceSettingGuardSchema = z.object({
  /**
   * `lifeline` — writing it can cut the router off from the ACS (the ACS URL,
   * its credentials, the TR-069 connection). `editor` — a named panel owns it
   * because the platform records it and re-applies it (the subscriber WAN), or
   * sets it itself (the ProvisioningCode label is the ONU's name). `action` —
   * writing it restarts, resets or reconfigures the whole router; the page's
   * own actions do that, with their confirmations.
   */
  kind: z.enum(['lifeline', 'editor', 'action']),
  reason: z.string(),
  /** The Configs panel that changes it, when there is one. */
  panel: z.enum(['wan', 'wifi', 'lan', 'management', 'rename']).nullable(),
});
export type DeviceSettingGuard = z.infer<typeof deviceSettingGuardSchema>;

export const deviceSettingLeafSchema = z.object({
  path: z.string(),
  /** In words where the platform knows the field; the CPE's own name otherwise. */
  label: z.string(),
  /** The parameter's last segment, exactly as the CPE spells it. */
  name: z.string(),
  /** The XSD type the CPE declared: xsd:boolean, xsd:unsignedInt, xsd:string … */
  type: z.string(),
  /** Null for a secret (never returned) and for a value the ACS has not read. */
  value: z.union([z.string(), z.number(), z.boolean()]).nullable(),
  /** The CPE says it may be written. */
  writable: z.boolean(),
  /** A password-shaped leaf: write-only here, its value never sent to the browser. */
  secret: z.boolean(),
  guard: deviceSettingGuardSchema.nullable(),
  /** When the ACS last read it from the router. */
  readAt: z.string().nullable(),
});
export type DeviceSettingLeaf = z.infer<typeof deviceSettingLeafSchema>;

/** One TR-069 object — a WiFi network, a WAN connection, a LAN socket, the time service. */
export const deviceSettingObjectSchema = z.object({
  path: z.string(),
  title: z.string(),
  /** A line under the title: what this object is for, or why some of it is held. */
  note: z.string().nullable(),
  leaves: z.array(deviceSettingLeafSchema),
});
export type DeviceSettingObject = z.infer<typeof deviceSettingObjectSchema>;

/** A functional area of the router: WiFi, WAN, LAN, Time, VoIP, vendor extras … */
export const deviceSettingAreaSchema = z.object({
  key: z.string(),
  label: z.string(),
  objects: z.array(deviceSettingObjectSchema),
  /** Leaves the CPE lets us write here, guards excluded. */
  changeable: z.number().int(),
  total: z.number().int(),
});
export type DeviceSettingArea = z.infer<typeof deviceSettingAreaSchema>;

export const deviceSettingsSchema = z.object({
  deviceId: z.string(),
  /** `InternetGatewayDevice` (TR-098) or `Device` (TR-181). */
  root: z.string().nullable(),
  lastInform: z.string().nullable(),
  areas: z.array(deviceSettingAreaSchema),
  total: z.number().int(),
  changeable: z.number().int(),
});
export type DeviceSettings = z.infer<typeof deviceSettingsSchema>;

/** Body of POST /devices/:id/settings — leaves by path, each typed from the CPE's own declaration. */
export const setDeviceSettingsSchema = z.object({
  values: z
    .array(
      z.object({
        path: z.string().min(1).max(400),
        value: z.union([z.string(), z.number(), z.boolean()]),
      }),
    )
    .min(1)
    .max(64),
});
export type SetDeviceSettingsRequest = z.infer<typeof setDeviceSettingsSchema>;

export interface SetDeviceSettingsResult {
  delivered: boolean;
  written: string[];
  message: string;
}
